import { createSelector } from '@ngrx/store';

import { partition } from 'lodash';

import { selectCustomerStepControlStatus, selectLocationStepControlStatus } from 'src/app/new-opportunity/step-navigation/step-navigation.component';
import { ActionSource } from 'src/app/state/action-sources';
import { selectChanges } from 'src/app/state/unsaved-changes.selectors';

import { selectJobEditDisabledWarning, selectJobId, UNSAVEABLE_JOB_PAGE_CHANGE_SOURCES } from '../../job/job-tool.reducer';
import { crewPayAssignmentChangeKey } from '../../job/job-workorders/mr-calculator/crew-pay-table/shared-crew-pay';
import { newOpportunityFeature } from '../../new-opportunity/new-opportunity.feature';
import { createCheckSinglePermissionSelector } from '../../state/branding.store';
import { selectUrl } from '../../state/router';

export const selectJobPageChanges = createSelector(
    selectJobId,
    selectChanges,
    (jobId, changes) => changes.filter((c) => Boolean(c.jobId) && c.jobId === jobId),
);

export const selectBannerChanges = createSelector(
    selectUrl,
    selectJobId,
    selectChanges,
    (url, jobId, changes) => {

      if (url?.startsWith('/jobs/new')) {
        return changes.filter((c) => c.source === ActionSource.NEW_OPPORTUNITY_V4);
      }

      return changes.filter((c) => Boolean(c.jobId) && c.jobId === jobId && !UNSAVEABLE_JOB_PAGE_CHANGE_SOURCES.includes(c.source));
    },
);

export const selectSaveableJobPageChanges = createSelector(
    selectJobPageChanges,
    (changes) => changes.filter((c) => !UNSAVEABLE_JOB_PAGE_CHANGE_SOURCES.includes(c.source)),
);

// Requirement: `#6951` - on a job shared with the current zone, the Crew Pay assignment boxes are
// saved by setCrewPayAssignments and every other change by the full job save. These two
// selectors split the saveable changes between them, so no change is ever saved twice.
const selectJobIsSharedWithCurrentZone = createSelector(
    selectJobEditDisabledWarning,
    (warning) => warning?.type === 'shared',
);

export const selectSharedCrewPayChanges = createSelector(
    selectSaveableJobPageChanges,
    selectJobIsSharedWithCurrentZone,
    (changes, shared) => shared ? changes.filter((c) => crewPayAssignmentChangeKey(c)) : [],
);

/**
 * Whether this user may tick the Crew Pay boxes of a job shared with the current zone, which the
 * shared-job overlay otherwise covers. Both the crew-pay table and this banner lift themselves
 * above that overlay on it. Visibility only: setCrewPayAssignments checks the share and the
 * permission again.
 */
export const selectCanEditSharedCrewPay = createSelector(
    selectJobIsSharedWithCurrentZone,
    createCheckSinglePermissionSelector({ permission: 'jobCrewPay.editShared' }),
    (shared, allowed) => shared && Boolean(allowed),
);

export const selectJobSaveChanges = createSelector(
    selectSaveableJobPageChanges,
    selectJobIsSharedWithCurrentZone,
    (changes, shared) => shared ? changes.filter((c) => !crewPayAssignmentChangeKey(c)) : changes,
);

export const selectJobPageHasSaveableChanges = createSelector(
    selectSaveableJobPageChanges,
    (changes) => changes.length > 0,
);

export const selectSaveChangesButtonDisabledReason = createSelector(
    selectUrl,
    selectBannerChanges,
    newOpportunityFeature.selectStep,
    selectCustomerStepControlStatus,
    selectLocationStepControlStatus,
    (url, changes, step, customerStatus, locationStatus) => {
        if (url?.startsWith('/jobs/new') && step === 'customer') {

          const validationErrors = Object
            .values(customerStatus)
            .map(({ error }) => error)
            .filter(Boolean);

          if (validationErrors.length > 0) {
            return `Cannot save changes: ${validationErrors[0]}`;
          }
        }

        if (url?.startsWith('/jobs/new') && step === 'locations') {
          const validationErrors = Object
            .values(locationStatus)
            .map(({ error }) => error)
            .filter(Boolean);

          if (validationErrors.length > 0) {
            return `Cannot save changes: ${validationErrors[0]}`;
          }
        }

        const [ pendingChanges, newChanges ] = partition(changes, (c) => c.saving);

        if (newChanges.length === 0 && pendingChanges.length > 0) {
            return 'Saving...';
        }

        return '';
    }
);
